import {
  Button, Grid, Table, TableBody, TableCell, TableContainer,
  TableRow, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, TableHead,
} from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Plus, Trash01 } from '../../assets';
import { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../components/shared/useUser';
import { TaskCategoriesClient, TaskCategoryDto } from '../../app/services/api/tasksClient';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import CustomPagination from '../../components/forms/Pagination';

const PAGE_SIZE = 10;

const client = TaskCategoriesClient;

const ManageTaskCategories = () => {
  const { t } = useTranslation('tasks');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasPermissionTo } = useContext(UserContext);
  const canCreate = hasPermissionTo ? hasPermissionTo(['TaskCategoriesCreate']) : false;
  const canEdit = hasPermissionTo ? hasPermissionTo(['TaskCategoriesEdit']) : false;
  const canDelete = hasPermissionTo ? hasPermissionTo(['TaskCategoriesDelete']) : false;

  const [categories, setCategories] = useState<TaskCategoryDto[]>([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openDeleteId, setOpenDeleteId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState({ name: '', description: '', sortOrder: 10 });
  const [errors, setErrors] = useState<{ name?: string; description?: string; sortOrder?: string }>({});
  const [page, setPage] = useState(0);

  useEffect(() => {
    client.getCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  // lazy: pagination is client-side over the already-loaded mock list; upgrade to server-side paging if the categories list grows large.
  const pagedCategories = categories.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const maxPages = Math.ceil(categories.length / PAGE_SIZE);

  const openCreateDialog = () => {
    if (!canCreate) return;
    setFormValues({ name: '', description: '', sortOrder: 10 });
    setEditId(null);
    setOpenCreate(true);
  };
  const openEditDialog = (row: TaskCategoryDto) => {
    if (!canEdit) return;
    setFormValues({ name: row.name, description: row.description, sortOrder: 10 });
    setEditId(row.id);
    setOpenCreate(true);
  };

  const validate = () => {
    const e: typeof errors = {};
    if (!formValues.name.trim()) e.name = t('categories.validation.required', 'Required');
    if (isNaN(formValues.sortOrder)) e.sortOrder = t('categories.validation.invalid', 'Invalid');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const dto: TaskCategoryDto = {
      id: editId ?? 0,
      franchiseId: 1,
      name: formValues.name,
      description: formValues.description,
    };

    if (editId === null) {
      await client.createCategory(dto);
    } else {
      await client.updateCategory(editId, dto);
    }

    const updated = await client.getCategories();
    setCategories(updated);
    handleClose();
  };

  const handleClose = () => { setOpenCreate(false); setOpenDeleteId(null); };

  const handleDelete = async (id: number) => {
    await client.deleteCategory(id);
    const updated = await client.getCategories();
    setCategories(updated);
    setOpenDeleteId(null);
  };

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}><PageTitle>{t('categories.title', 'Task Categories')}</PageTitle></Grid>
        <Grid item
          xs={isMobile ? 8 : 9}></Grid>
        <Grid item
          xs={isMobile ? 4 : 3}
          sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {canCreate && <Button size='lg'
            sx={{ mr: 3 }}
            variant='primary'
            onClick={openCreateDialog}><Plus />{t('categories.createNew', 'Create New')}</Button>}
        </Grid>
        <Grid item
          xs={12}>
          <Section>
            <TableHeader>
              {t('categories.title', 'Task Categories')}
              <Pill text={t('categories.itemCount', '{{count}} Categories', { count: categories.length })}></Pill>
            </TableHeader>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('categories.columnName', 'Name')}</TableCell>
                    <TableCell>{t('categories.columnDescription', 'Description')}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedCategories.map(row => (
                    <TableRow key={row.id}
                      onClick={() => openEditDialog(row)}
                      hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.description}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}
                        sx={{ display: 'flex', gap: 1 }}>
                        {canDelete && (
                          <Trash01
                            color={theme.palette.error[500]}
                            className='delete-icon'
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenDeleteId(row.id); }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {categories.length === 0 && <TableRow><TableCell colSpan={3}>{t('categories.noCategoriesFound', 'No categories found.')}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={maxPages}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          </Section>
        </Grid>
      </Grid>
      <Dialog open={openCreate && (canCreate || canEdit)}
        onClose={handleClose}
        maxWidth='sm'
        fullWidth>
        <DialogTitle>{editId === null ? t('categories.createDialogTitle', 'Create Category') : t('categories.editDialogTitle', 'Edit Category')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label={t('categories.fieldName', 'Name')}
            value={formValues.name}
            onChange={e => setFormValues(v => ({ ...v, name: e.target.value }))}
            error={!!errors.name}
            helperText={errors.name}
          />
          <TextField
            label={t('categories.fieldDescription', 'Description')}
            multiline
            minRows={3}
            value={formValues.description}
            onChange={e => setFormValues(v => ({ ...v, description: e.target.value }))}
          />
          <TextField
            label={t('categories.fieldSortOrder', 'Sort Order')}
            type='number'
            value={formValues.sortOrder}
            onChange={e => setFormValues(v => ({ ...v, sortOrder: parseInt(e.target.value, 10) }))}
            error={!!errors.sortOrder}
            helperText={errors.sortOrder}
          />
        </DialogContent>
        <DialogActions>
          <Button variant='secondary'
            onClick={handleClose}>{t('categories.cancel', 'Cancel')}</Button>
          <Button variant='primary'
            onClick={handleSave}>{t('categories.save', 'Save')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openDeleteId !== null && canDelete}
        onClose={() => setOpenDeleteId(null)}
        maxWidth='xs'
        fullWidth>
        <DialogTitle>{t('categories.deleteDialogTitle', 'Delete Category')}</DialogTitle>
        <DialogContent>{t('categories.deleteConfirm', 'Are you sure you want to delete this category?')}</DialogContent>
        <DialogActions>
          <Button variant='secondary'
            onClick={() => setOpenDeleteId(null)}>{t('categories.cancel', 'Cancel')}</Button>
          <Button color='warning'
            onClick={() => openDeleteId !== null && handleDelete(openDeleteId)}>{t('categories.delete', 'Delete')}</Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default ManageTaskCategories;